import { normalizeApiError } from "../../../../lib/result-codes";
import type { RedeemErrorKind } from "../types/merchantCouponRedeemer.types";

/**
 * Extract the JWT token from a coupon QR value.
 * The resident QR image encodes either a raw token or a URL carrying it
 * as the `token` query parameter.
 */
export const extractTokenFromQRCode = (rawValue: string): string => {
  try {
    if (rawValue.includes("?token=") || rawValue.includes("&token=")) {
      const url = new URL(rawValue);
      const token = url.searchParams.get("token");
      if (token) return token;
    }
    return rawValue;
  } catch {
    return rawValue;
  }
};

/**
 * Map a verify-qr failure to a stable UI error state:
 * invalid/expired QR (400), coupon outside the merchant's scope (404),
 * previously/concurrently used coupon (409).
 */
export const getRedeemErrorKind = (error: unknown): RedeemErrorKind => {
  const status = normalizeApiError(error).http_request_status;
  if (status === 400) return "invalidOrExpired";
  if (status === 404) return "outOfScope";
  if (status === 409) return "alreadyUsed";
  return "generic";
};

export const redeemErrorTitles: Record<RedeemErrorKind, string> = {
  invalidOrExpired: "This QR code is invalid or expired.",
  outOfScope: "This coupon does not belong to your store.",
  alreadyUsed: "This coupon has already been redeemed.",
  generic: "Could not redeem this coupon.",
};

export const formatRedeemedAt = (isoString: string): string => {
  if (!isoString) return "";
  try {
    return new Date(isoString).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return "";
  }
};
